import {mapGetters} from 'vuex';
import {Severity} from '@sentry/browser';

import PgApi from '../api/pg-api';
import MixinSentryLogger from './sentry-logger';
import * as mutations from '../store/mutations';

export default {
  name: 'MixinAspsp',
  computed: {
    ...mapGetters([
      'affiliateConfig',
      'prefillData',
      'walletUid',
      'sfId',
      'countryExcluded',
    ])
  },
  data() {
    return {
      pgApi: new PgApi()
    };
  },
  mixins: [
    MixinSentryLogger
  ],
  methods: {
    /**
     * Retrieve contact data from Salesforce then prefill form.
     * @param {object} context Context (this)
     * @param {function} afterPrefillForm Function to execute after filling form with data
     */
    getPrefillDataWithSfId(context, afterPrefillForm) {
      /**
       * @todo Handle walletId (walletUid data are prioritary on sfId)
       */
      if (this.walletUid) {
        console.log('[vanguard] walletUid provided, aborting prefill based on sfId.');
        this.prefillForm(context);
        return;
      }

      if (!this.sfId) {
        console.log('[vanguard] sfId missing');
        return;
      }

      context.isFormLoading = true;

      let timeout = false;
      setTimeout(() => {
        timeout = true;
        context.isFormLoading = false;
      }, 7000);

      this.pgApi.getPrefillData(this.sfId, this.affiliateConfig.affiliateId).then(data => {
        this.$log.debug('Prefill data', data.content);
        this.$store.commit(mutations.SET_PREFILL_DATA, data.content);
        this.$store.commit(mutations.SET_ASPSP_EMAIL, data.content.EMAIL);

        if (timeout) {
          this.sendSentryLog(Severity.Warning, 'Timeout while retrieving prefill data');
          return;
        }

        /**
         * Prefill form
         */
        this.prefillForm(context);

        /**
         * Validate form etc
         */
        afterPrefillForm();

        context.isFormLoading = false;
      }).catch(error => {
        context.isFormLoading = false;
        this.sendSentryLog(Severity.Error, 'Unable to retrieve prefill data', {error});
      });
    },
    /**
     * Prefill form
     * @param {object} context Context (this)
     */
    prefillForm(context) {
      const country = this.$data.billingCountry;
      this.$store.commit(mutations.SET_COUNTRIEXCLUDED, this.localConfig && this.localConfig.countriesExcluded.length > 0 && this.localConfig.countriesExcluded.includes(country));

      if (this.countryExcluded) {
        this.$store.commit(mutations.SET_IS_ASPSP_PREFILL_EDITED_BY_USER, true);
        this.$store.commit(mutations.SET_IS_ASPSP_ELIGIBLE, false);
        console.log(`[vanguard] The country ${country} is excluded. Prefilling only necessary data.`);
      }

      context.billingEmail = context.billingEmail || this.prefillData.EMAIL;
      context.billingEmailConfirm = context.billingEmail || this.prefillData.EMAIL;
      context.billingFirstName = context.billingFirstName || this.prefillData.FIRSTNAME;
      context.billingLastName = context.billingLastName || this.prefillData.LASTNAME;
      context.billingPhoneNumber = context.billingPhoneNumber || this.prefillData.PHONE;
      context.shippingPhoneNumber = this.prefillData.SHIPPINGPHONE__C ? this.prefillData.SHIPPINGPHONE__C : this.prefillData.PHONE;
      context.shippingFirstName = this.prefillData.SHIPPINGFIRSTNAME ? this.prefillData.SHIPPINGFIRSTNAME : this.prefillData.FIRSTNAME;
      context.shippingLastName = this.prefillData.SHIPPINGLASTNAME ? this.prefillData.SHIPPINGLASTNAME : this.prefillData.LASTNAME;

      if (!this.countryExcluded) {
        context.billingGender = this.prefillData.GENDER || null;
        context.shippingGender = this.prefillData.GENDER || null;
        context.billingStreet1 = this.prefillData.BILLINGSTREET1__C;
        context.billingStreet2 = this.prefillData.BILLINGSTREET2__C;
        context.billingStreet3 = this.prefillData.BILLINGSTREET3__C;
        context.billingStreet4 = this.prefillData.BILLINGSTREET4__C;
        context.billingZipCode = this.prefillData.BILLINGZIPCODE__C;
        context.billingCity = this.prefillData.BILLINGCITY__C;
        context.billingCountry = this.prefillData.BILLINGCOUNTRY__C;
        context.shippingStreet1 = this.prefillData.SHIPPINGSTREET1__C ? this.prefillData.SHIPPINGSTREET1__C : this.prefillData.BILLINGSTREET1__C;
        context.shippingStreet2 = this.prefillData.SHIPPINGSTREET2__C ? this.prefillData.SHIPPINGSTREET2__C : this.prefillData.BILLINGSTREET2__C;
        context.shippingStreet3 = this.prefillData.SHIPPINGSTREET3__C ? this.prefillData.SHIPPINGSTREET3__C : this.prefillData.BILLINGSTREET3__C;
        context.shippingStreet4 = this.prefillData.SHIPPINGSTREET4__C ? this.prefillData.SHIPPINGSTREET4__C : this.prefillData.BILLINGSTREET4__C;
        context.shippingZipCode = this.prefillData.SHIPPINGZIPCODE__C ? this.prefillData.SHIPPINGZIPCODE__C : this.prefillData.BILLINGZIPCODE__C;
        context.shippingCity = this.prefillData.SHIPPINGCITY__C ? this.prefillData.SHIPPINGCITY__C : this.prefillData.BILLINGCITY__C;
        context.shippingCountry = this.prefillData.SHIPPINGCOUNTRY__C ? this.prefillData.SHIPPINGCOUNTRY__C : this.prefillData.BILLINGCOUNTRY__C;
      }

      this.$store.commit(mutations.SET_ASPSP_EMAIL, context.billingEmail);
      this.$store.commit(mutations.SET_IS_ASPSP_PREFILL_EDITED_BY_USER, false);
    },
    /**
     * Save the entire form data into the global store.
     * @param {object} context Context (this)
     */
    storeFormData(context) {
      this.$store.commit(mutations.SET_FORM_DATA, {
        billing: {
          billingEmail: context.billingEmail,
          billingEmailConfirm: context.billingEmailConfirm,
          billingGender: context.billingGender,
          billingFirstName: context.billingFirstName,
          billingLastName: context.billingLastName,
          billingPhoneNumber: context.billingPhoneNumber,
          billingStreet1: context.billingStreet1,
          billingStreet2: context.billingStreet2,
          billingStreet3: context.billingStreet3,
          billingStreet4: context.billingStreet4,
          billingZipCode: context.billingZipCode,
          billingCity: context.billingCity,
          billingCountry: context.billingCountry
        },
        useSameAddressForShipping: true,
        shipping: {
          shippingGender: context.shippingGender,
          shippingFirstName: context.shippingFirstName,
          shippingLastName: context.shippingLastName,
          shippingPhoneNumber: context.shippingPhoneNumber,
          shippingStreet1: context.shippingStreet1,
          shippingStreet2: context.shippingStreet2,
          shippingStreet3: context.shippingStreet3,
          shippingStreet4: context.shippingStreet4,
          shippingZipCode: context.shippingZipCode,
          shippingCity: context.shippingCity,
          shippingCountry: context.shippingCountry
        }
      });
    }
  }
};



// WEBPACK FOOTER //
// ./src/common/mixins/contact-form.js